import React from "react";
import { Plus } from "lucide-react";
import { Button } from "../../../components/ui";

/**
 * What stands on the Form tab while the workspace has nothing to sell.
 *
 * Both of this tab's forms end in a choice out of the practice's own lists: an
 * enquiry names the plan somebody is asking about, a pre-consultation is
 * prepared against the service they are coming for. With neither list filled,
 * the form a client opens has an empty picker in the middle of it and no way to
 * be finished — so the address is withheld rather than handed out and then
 * found to be broken, which is a link already sitting in an Instagram bio by
 * the time anybody notices.
 *
 * It replaces the card on both sides of the preview switch, because what is
 * missing is missing for both. The phone beside it is left alone: it is a
 * picture of the form's own welcome screen, it is true whether or not there is
 * a plan on the books, and taking it down as well would leave the tab with
 * nothing on it to explain what is being set up.
 *
 * Written as the next step and not as a refusal — the same reading
 * `PlansRequiredModal` takes of the same absence. A workspace on its first day
 * has no plans yet, which is where everybody starts, so there is no red and no
 * "cannot"; there are the two doors, and they are one press away.
 */

export interface FormSetupRequiredCardProps {
  /** Opens the Plans tab, which is where a plan is made. */
  onCreatePlan: () => void;
  /** Opens the Services tab, the same way. */
  onCreateService: () => void;
}

const FormSetupRequiredCard: React.FC<FormSetupRequiredCardProps> = ({
  onCreatePlan,
  onCreateService,
}) => (
  // The link card's own shell, sized by what is in it rather than run to the
  // bottom of the column: this card carries a mark, two lines and two buttons,
  // and a card stretched to the height of the phone beside it would be mostly
  // empty white with the point of it stranded in the middle.
  //
  // The generous top and bottom padding is the height: it sets the block in
  // its own room without the card having to reach for a height it has nothing
  // to fill with.
  <div className="flex flex-col rounded-md border border-line bg-white px-5 py-16 sm:px-6 sm:py-24 3xl:px-8">
    {/* Amber rather than the brand teal, and the app's own warning pair — see
        `Toast`. Teal here would read as an action offered; the two buttons
        below are the actions, and this is the mark on what is missing. */}
    <span
      aria-hidden="true"
      className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FFF4E5] text-[#FF9500]"
    >
      <Plus className="h-6 w-6" />
    </span>

    <h3 className="mt-6 text-[22px] 3xl:text-[26px] font-semibold text-[#00424B]">
      Add the services &amp; plans you offer
    </h3>
    <p className="mt-2 max-w-[420px] text-[14px] 3xl:text-[15px] leading-6 text-ink-muted">
      Clients use your enquiry form to select bookings. Without services or
      plans, the form is empty, so the link remains locked.
    </p>

    {/* Both doors, in the order the tabs are in and the order the dashboard's
        setup checklist walks. The service is the filled one: it is what the
        Goal field on both forms is drawn from, so it is the half that a form
        cannot be answered without at all. */}
    <div className="mt-6 flex flex-wrap items-center gap-3">
      <Button
        variant="outline"
        size="md"
        startIcon={<Plus className="h-4 w-4" />}
        onClick={onCreatePlan}
      >
        Create plan
      </Button>
      <Button
        size="md"
        startIcon={<Plus className="h-4 w-4" />}
        onClick={onCreateService}
      >
        Create service
      </Button>
    </div>
  </div>
);

export default FormSetupRequiredCard;
